import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export interface OsWindowMenuAction {
  label: string;
  onOpen: () => void;
}

type OsWindowMenuActionRegistrar = (action: OsWindowMenuAction) => () => void;

export const OsWindowMenuActionContext =
  createContext<OsWindowMenuActionRegistrar | null>(null);

/** Keeps the newest still-mounted registration active in the shell. */
export const useOsWindowMenuActionRegistry = () => {
  const [menuAction, setMenuAction] = useState<OsWindowMenuAction | null>(null);

  const registrationsRef = useRef<
    Array<{ id: symbol; action: OsWindowMenuAction }>
  >([]);

  const registerMenuAction = useCallback((action: OsWindowMenuAction) => {
    const registration = { id: Symbol('os-window-menu-action'), action };
    registrationsRef.current.push(registration);
    setMenuAction(action);

    return () => {
      const registrationIndex = registrationsRef.current.findIndex(
        (candidate) => candidate.id === registration.id
      );

      if (registrationIndex === -1) return;

      registrationsRef.current.splice(registrationIndex, 1);

      const registrations = registrationsRef.current;

      setMenuAction(
        registrations.length > 0
          ? registrations[registrations.length - 1].action
          : null
      );
    };
  }, []);

  return { menuAction, registerMenuAction };
};

/**
 * Registers a hamburger menu action in the default OS window chrome (shown
 * when a page's side panel is collapsed to a drawer). Cleared automatically
 * on unmount or when `action` becomes null.
 *
 * Returns whether the window header is showing the control, so the page can
 * leave out its own inline trigger instead of rendering both — the same
 * contract `useOsWindowBackAction` has. Outside the shell (no provider) the
 * page's own trigger is the only way to the panel.
 */
export const useOsWindowMenuAction = (
  action: OsWindowMenuAction | null
): boolean => {
  const registerMenuAction = useContext(OsWindowMenuActionContext);
  const actionRef = useRef(action);
  actionRef.current = action;

  const isActive = action !== null;
  const label = action?.label;

  useEffect(() => {
    if (!registerMenuAction || !isActive || label === undefined) return;

    return registerMenuAction({
      label,
      onOpen: () => actionRef.current?.onOpen(),
    });
  }, [isActive, label, registerMenuAction]);

  return isActive && registerMenuAction !== null;
};
